import {
  EVENT_CATEGORIES,
  EVENT_SPACES,
  type EventCategory,
  type EventMediaItem,
  type EventSpace,
} from '@mds/shared/types/event';

import GalleryPicker from '@/components/files/gallery-picker';
import LineEditor from '@/components/events/line-editor';
import Field, { inputCls, selectCls } from '@/components/ui/field';
import Select from '@mds/shared/ui/select';
import type { EventDraft } from '@/domain/events';
import type { FieldBinder } from '@/lib/validation';

/**
 * The create and edit forms are the same fields against the same draft, so they
 * are one component — an event is edited as a whole rather than field by field.
 */
export default function EventForm({
  draft,
  onChange,
  known = [],
  field,
}: {
  draft: EventDraft;
  onChange: (next: EventDraft) => void;
  /** The event's current gallery, when editing one. */
  known?: readonly EventMediaItem[];
  /** The parent form's validation, checked against `eventSchema`. */
  field: FieldBinder;
}) {
  const set = (patch: Partial<EventDraft>) => onChange({ ...draft, ...patch });

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" required {...field('title')}>
          <input
            className={inputCls}
            value={draft.title}
            maxLength={160}
            onChange={(e) => set({ title: e.target.value })}
          />
        </Field>
        <Field label="Category" required {...field('category')}>
          <Select
            variant="field"
            className={selectCls}
            value={draft.category}
            onChange={(category) => set({ category: category as EventCategory })}
            options={EVENT_CATEGORIES.map((c) => ({ value: c, label: c }))}
            aria-label="Category"
          />
        </Field>
        <Field label="Date" required {...field('eventDate')}>
          <input
            type="date"
            className={inputCls}
            value={draft.eventDate}
            onChange={(e) => set({ eventDate: e.target.value })}
          />
        </Field>
        <Field label="Space" required {...field('space')}>
          <Select
            variant="field"
            className={selectCls}
            value={draft.space}
            onChange={(space) => set({ space: space as EventSpace })}
            options={EVENT_SPACES.map((sp) => ({ value: sp, label: sp }))}
            aria-label="Space"
          />
        </Field>
        <Field label="Starts" required {...field('start')}>
          <input
            type="time"
            className={inputCls}
            value={draft.start}
            onChange={(e) => set({ start: e.target.value })}
          />
        </Field>
        <Field label="Ends" required {...field('end')}>
          <input
            type="time"
            className={inputCls}
            value={draft.end}
            onChange={(e) => set({ end: e.target.value })}
          />
        </Field>
        <Field label="Seats" required {...field('capacity')}>
          <input
            type="number"
            min={1}
            className={inputCls}
            value={draft.capacity}
            onChange={(e) => set({ capacity: e.target.value })}
          />
        </Field>
        {/* Priced in dollars and sent in cents, so the field takes an amount
            rather than the "Free" / "$15" label the listing shows. */}
        <Field
          label="Price"
          hint="In dollars. Leave it empty for a free event."
          {...field('price')}
        >
          <input
            inputMode="decimal"
            className={inputCls}
            value={draft.price}
            placeholder="0.00"
            onChange={(e) => set({ price: e.target.value })}
          />
        </Field>
      </div>

      <GalleryPicker
        value={draft.mediaFileIds}
        onChange={(mediaFileIds) => set({ mediaFileIds })}
        known={known}
        {...field('mediaFileIds', { within: true })}
      />

      <Field
        label="Short description"
        hint="A line or two, for listings and cards."
        required
        {...field('shortDescription')}
      >
        <textarea
          rows={2}
          maxLength={500}
          className={inputCls}
          value={draft.shortDescription}
          onChange={(e) => set({ shortDescription: e.target.value })}
        />
      </Field>

      <Field
        label="Long description"
        hint="The full write-up on the event page."
        {...field('longDescription')}
      >
        <textarea
          rows={5}
          className={inputCls}
          value={draft.longDescription}
          onChange={(e) => set({ longDescription: e.target.value })}
        />
      </Field>

      <Field
        label="Price note"
        hint="Anything the price needs saying about it."
        {...field('priceNote')}
      >
        <input
          className={inputCls}
          maxLength={500}
          placeholder="Paid at the front desk."
          value={draft.priceNote}
          onChange={(e) => set({ priceNote: e.target.value })}
        />
      </Field>

      <LineEditor
        label="Run of the evening"
        hint="What happens and when. Left empty, the event page omits the section."
        columns={['Time', 'What happens']}
        firstType="time"
        firstWidth="w-[150px]"
        value={draft.agenda}
        onChange={(agenda) => set({ agenda })}
        path="agenda"
        field={field}
      />

      <LineEditor
        label="Good to know"
        hint="Who it is for, what to bring, parking, language: whatever needs saying."
        columns={['Label', 'Detail']}
        value={draft.goodToKnow}
        onChange={(goodToKnow) => set({ goodToKnow })}
        path="goodToKnow"
        field={field}
      />
    </>
  );
}
